<div class="mx-auto max-w-(--breakpoint-2xl) px-3">
  <div class="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">

    <!-- Header -->
    <div class="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between p-6">
      <div class="flex-1 space-y-4">
        <div class="flex items-center gap-3 text-gray-500 dark:text-gray-400 text-sm">
          <lucide-icon [name]="PackageIcon" size="18"></lucide-icon>
          Product Overview
        </div>
        <div>
          <div class="flex flex-wrap items-center gap-2">
            <h1 class="text-3xl font-semibold text-gray-900 dark:text-white">
              {{ product.name }}
            </h1>
            @if (product.id) {
              <span
                class="inline-flex items-center text-gray-500 dark:text-gray-400 cursor-help"
                [appTippy]="'Product ID: ' + product.id"
              >
                <lucide-icon [name]="InfoIcon" size="14"></lucide-icon>
              </span>
            }
          </div>
          @if (descriptionHtml) {
            <div class="mt-2 text-gray-500 dark:text-gray-400 prose prose-sm dark:prose-invert max-w-none"
                 [innerHTML]="descriptionHtml">
            </div>
          } @else {
            <p class="mt-2 text-gray-500 dark:text-gray-400">
              No description has been provided for this product yet.
            </p>
          }
        </div>
        <div class="flex flex-wrap gap-3">
          <app-badge
            [text]="product.category"
            [color]="ProductModelUtils.getCategoryColor(product.category)">
          </app-badge>
          <app-badge
            [text]="product.status"
            [color]="ProductModelUtils.getStatusColor(product.status)">
          </app-badge>
          <app-badge
            *ngIf="destinedForText"
            [text]="'Destined for: ' + destinedForText"
            color="secondary">
          </app-badge>
        </div>
        <div class="flex flex-wrap items-center gap-4">
          <div class="text-4xl font-semibold text-gray-900 dark:text-white">
            {{
              product['price_formatted']
                || (product.price !== null ? (product.price | currency:'GBP') : 'Price TBD')
            }}
          </div>
          <div class="text-sm text-gray-500 dark:text-gray-400" *ngIf="product.is_variant_based">
            Variant-based pricing
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-3 w-full max-w-sm">
        <img
          *ngIf="heroImage; else placeholderImage"
          [src]="heroImage!"
          [alt]="product.name"
          class="rounded-2xl border border-gray-100 dark:border-gray-800 object-cover w-full">
        <ng-template #placeholderImage>
          <div class="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 p-10 text-center text-gray-400">
            No image available
          </div>
        </ng-template>
        @if (isAdmin) {
          <app-button
            [label]="'Edit product'"
            [icon]="EditIcon"
            [style]="'primary'"
            (click)="editProduct()">
          </app-button>
        }
        <app-button
          [label]="'Back to products'"
          [icon]="ArrowLeftIcon"
          [style]="'secondary'"
          (click)="backToList()">
        </app-button>
      </div>
    </div>

    <!-- Detail Sections -->
    <div class="border-t border-gray-100 dark:border-gray-800 p-6">
      <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section>
          <div class="flex items-center gap-3 mb-4">
            <lucide-icon [name]="TagIcon" size="18" class="text-gray-500 dark:text-gray-400"></lucide-icon>
            <h2 class="text-lg font-medium text-gray-900 dark:text-white">Key Attributes</h2>
          </div>
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Category</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ product.category }}</p>
            </div>
            <div>
              <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Status</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ product.status }}</p>
            </div>
            <div>
              <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Minimum Spend</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ formatBoolean(product.is_minimum_spend) }}</p>
            </div>
            <div>
              <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Counts to 500 Deal</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ formatBoolean(product.counts_to_500_deal) }}</p>
            </div>
            <div>
              <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Included in 150 Free</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ formatBoolean(product.inc_in_150_free) }}</p>
            </div>
            <div>
              <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Variant Based</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ formatBoolean(product.is_variant_based || false) }}</p>
            </div>
          </div>
        </section>

        <section>
          <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Additional Information</h2>
          <div class="space-y-3 text-sm text-gray-600 dark:text-gray-300">
            <div class="flex justify-between">
              <span>In Stock</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ formatBoolean(product.in_stock) }}</span>
            </div>
            <div class="flex justify-between">
              <span>Price Editable</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ formatBoolean(product.price_can_be_changed) }}</span>
            </div>
            <div class="flex justify-between" *ngIf="destinedForText">
              <span>Destined For</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ destinedForText }}</span>
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- Variants -->
    <div class="border-t border-gray-100 dark:border-gray-800 p-6">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-lg font-medium text-gray-900 dark:text-white">Variants</h2>
        <span class="text-sm text-gray-500 dark:text-gray-400">{{ product.variants.length }} configured</span>
      </div>

      <ng-container *ngIf="hasVariants(); else noVariants">
        <div class="space-y-4">
          <div
            class="rounded-xl border border-gray-100 p-4 dark:border-gray-800"
            *ngFor="let variant of product.variants">
              <div class="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div class="flex flex-wrap items-center gap-2">
                    <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ variant.name }}</p>
                    <span
                      class="inline-flex items-center text-gray-400 dark:text-gray-500 cursor-help"
                      [appTippy]="'Variant ID: ' + variant.id"
                    >
                      <lucide-icon [name]="InfoIcon" size="12"></lucide-icon>
                    </span>
                  </div>
                  <p class="text-xs text-gray-500 dark:text-gray-400">{{ variant.options.length }} options</p>
                </div>
              </div>


            <div class="mt-3 grid gap-3 sm:grid-cols-2" *ngIf="variant.options.length">
              <div
                class="rounded-lg border border-gray-100 p-3 text-sm dark:border-gray-800"
                *ngFor="let option of variant.options">
                <div class="flex flex-wrap items-center gap-2">
                  <p class="font-medium text-gray-900 dark:text-white">
                    {{ option.name }}
                  </p>
                  <span
                    class="inline-flex items-center text-gray-400 dark:text-gray-500 cursor-help"
                    [appTippy]="'Option ID: ' + option.id"
                  >
                    <lucide-icon [name]="InfoIcon" size="12"></lucide-icon>
                  </span>
                </div>
                <p class="text-gray-500 dark:text-gray-400">
                  {{ option.price !== null ? (option.price | currency:'GBP') : 'Price not specified' }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </ng-container>

      <ng-template #noVariants>
        <div class="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          No variants configured for this product.
        </div>
      </ng-template>
    </div>
  </div>
</div>
